import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import { usePaymentTransactionReceipt } from '../hooks/usePaymentTransactions';
import { formatBillingDate, formatTransactionAmount } from '../lib/billingState';
import styles from './TransactionReceipt.module.css';

interface TransactionReceiptProps {
  transactionId: number;
  onClose: () => void;
}

// PARA-140 (AC-5) — квитанция по транзакции: все поля контракта
// GET /billing/transactions/{id}, включая provider_event_id (у возвратов —
// с префиксом refund_, PARA-130) и subscription_id.
// Модалка поверх экрана истории: список под ней не размонтируется, поэтому
// загруженные страницы и позиция скролла сохраняются (AC-5, T20).
export function TransactionReceipt({
  transactionId,
  onClose,
}: TransactionReceiptProps): JSX.Element {
  const { t, i18n } = useTranslation('billing');
  const query = usePaymentTransactionReceipt(transactionId);
  const receipt = query.data;

  // AC-5: закрытие по Esc — модалка перехватывает клавишу у экрана истории.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    // Клик по подложке закрывает модалку; на самом окне клик останавливается,
    // иначе выбор текста квитанции закрывал бы её.
    <div className={styles.scrim} data-testid="receipt-scrim" onClick={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={t('receipt.heading')}
        data-testid="transaction-receipt"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className={styles.heading}>{t('receipt.heading')}</h2>

        {query.isPending && <p className={styles.message}>{t('receipt.loading')}</p>}
        {query.isError && (
          <>
            <p className={styles.message} data-testid="receipt-error">
              {t('receipt.error')}
            </p>
            <Button variant="secondary" fullWidth onClick={() => void query.refetch()}>
              {t('history.retry')}
            </Button>
          </>
        )}

        {receipt !== undefined && (
          <dl className={styles.fields}>
            <div className={styles.field}>
              <dt className={styles.fieldName}>{t('receipt.id')}</dt>
              <dd className={styles.fieldValue} data-testid="receipt-transaction-id">
                {receipt.id}
              </dd>
            </div>
            <div className={styles.field}>
              <dt className={styles.fieldName}>{t('receipt.plan')}</dt>
              <dd className={styles.fieldValue}>{t(`plans.${receipt.plan}.name`)}</dd>
            </div>
            <div className={styles.field}>
              <dt className={styles.fieldName}>{t('receipt.amount')}</dt>
              <dd className={styles.fieldValue}>
                {formatTransactionAmount(receipt.amount, receipt.currency, i18n.language)}
              </dd>
            </div>
            <div className={styles.field}>
              <dt className={styles.fieldName}>{t('receipt.date')}</dt>
              <dd className={styles.fieldValue}>
                {formatBillingDate(receipt.occurred_at, i18n.language)}
              </dd>
            </div>
            <div className={styles.field}>
              <dt className={styles.fieldName}>{t('receipt.status')}</dt>
              <dd className={styles.fieldValue}>{t(`transactions.status.${receipt.status}`)}</dd>
            </div>
            <div className={styles.field}>
              <dt className={styles.fieldName}>{t('receipt.providerEventId')}</dt>
              <dd className={styles.fieldValue} data-testid="receipt-provider-event-id">
                {receipt.provider_event_id}
              </dd>
            </div>
            <div className={styles.field}>
              <dt className={styles.fieldName}>{t('receipt.subscriptionId')}</dt>
              <dd className={styles.fieldValue}>{receipt.subscription_id}</dd>
            </div>
          </dl>
        )}

        <Button variant="ghost" fullWidth onClick={onClose}>
          {t('receipt.close')}
        </Button>
      </div>
    </div>
  );
}
